import {
    Score,
    ScoreDimension,
    ScoreDimensionValue,
    ScoreValue,
    ScoreWithStatistics,
} from "src/api/client-api/post/scoreSchema";
import { Heading, Ssq } from "src/api/sure-api/ssq/useGetOneSsq";
import { PropertyBuilder, SsqPropertyWithIndexesKey, UserProperty } from "../Property/Builder/PropertyBuilder";
import { SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { scoresTreeMerge } from "../Property/ScoresTreeMerge/ScoresTreeMerge";
import { Post } from "src/api/client-api/post/postSchema";
import { ScoreService } from "../EntityBasedScore/ScoreService";

export type SummaryGlobal = {
    id: string;
    // id: ProductSummaryType;
    label: string;
    score: ScoreValue;
};

export type SummaryDetailItem = {
    id: number;
    label: string;
    score: ScoreValue;
};

export type SummaryDetail = Partial<
    Record<
        // ProductSummaryType,
        string,
        SummaryDetailItem[]
    >
>;
export type SummaryData = {
    totalScore: number;
    summary: SummaryGlobal[];
    detail: SummaryDetail;
};

type _InternalHeading = {
    heading: Heading;
    headingScore: ScoreDimensionValue<ScoreWithStatistics>;
};

type ScoresByID = Partial<Record<string, Score>> | undefined;
export type ScoreDetailBuilderConstructor = {
    scoreId: number;
    scorePrecision?: number;
    scores: {
        scores: ScoresByID;
        scoresMax?: ScoresByID;
        scoresMin?: ScoresByID;
        scoresAvg?: ScoresByID;
        scoresCount?: ScoresByID;
        prevScores?: ScoresByID;
    };
    post: Pick<Post, "campaign" | "id" | "elements">;
    ssq: Ssq;
};

// For PropertyBuilder
type ExtraPropertyKeys = Record<never, unknown>;
type PropertyWithIndexes = SsqPropertyWithIndexesKey<ExtraPropertyKeys>;

// Autocomplete without errors, but this will trigger esling error.
type DimensionKeys =
    // Core Dimensions for ScoreHeading
    | "headings"
    | "subheadings"

    // Property Builder related dimensions
    | "properties"
    | "subproperties"
    | "multivalues"

    // Probably shouldn't be here, since this is ScoreDetail (=ScoreHeadings)
    | "lca"

    // Summary keys
    | "components"
    | "ingredients"
    // eslint-disable-next-line
    | (string & {});

type PropertyMapping = {
    byProperty: Partial<Record<PropertyKey, SsqProperty>>;
    byHeadingId: Partial<Record<PropertyKey, SsqProperty[]>>;
};

export class ScoreDetailAbstract {
    public static drilldownDimension = <ScoreSkeleton = ScoreValue>(
        dimensions: ScoreDimension<ScoreSkeleton>[] | undefined,
        label: DimensionKeys,
    ) => {
        // Small but effective for this very repetitive task;
        // It allows to skip both the `.find()` and 2 exceptions:
        // - Dimension not found.
        // - No value in dimension.
        const dimensionValues = dimensions?.find((d) => d.label === label)?.values;
        if (dimensionValues === undefined) {
            return null;
        }
        return dimensionValues;
    };

    public getSummary = (): SummaryData | null => {
        console.warn("You called getSummary as it was not instantiated");
        return null;
    };
}

export class ScoreDetailBuilder extends ScoreDetailAbstract {
    private post: Pick<Post, "campaign" | "id" | "elements">;
    private ssq: Ssq;
    private precision: number;

    private _allPropertyByIds: Partial<Record<PropertyKey, SsqProperty>>;
    private _scoresWithStats: Score<ScoreWithStatistics> | null;

    public constructor(options: ScoreDetailBuilderConstructor) {
        super();

        const { scores: _scores, scoreId } = options;
        this.ssq = options.ssq;
        this.post = options.post;
        this.precision = options.scorePrecision ?? 0;

        const { scores, scoresAvg, scoresMax, scoresMin, scoresCount, prevScores } = _scores;
        const score = scores?.[scoreId];
        const scoreAvg = scoresAvg?.[scoreId];
        const scoreMax = scoresMax?.[scoreId];
        const scoreMin = scoresMin?.[scoreId];
        const scoreCount = scoresCount?.[scoreId];
        const prevScore = prevScores?.[scoreId];

        const mergedScores = scoresTreeMerge({
            scores: score,
            prevScores: prevScore,
            scoresAvg: scoreAvg,
            scoresMax: scoreMax,
            scoresMin: scoreMin,
            scoresCount: scoreCount,
        });
        this._scoresWithStats = mergedScores;

        const allPropertyByIds = this.preparePropertyIdMapping(options.ssq);
        this._allPropertyByIds = allPropertyByIds["byProperty"];
    }

    private handlePropertyDimension = (internalHeading: _InternalHeading): UserProperty<ExtraPropertyKeys>[] | null => {
        const { heading, headingScore } = internalHeading;
        const propertyScores = ScoreDetailBuilder.drilldownDimension(headingScore.dimensions, "properties");

        const propertyIds = heading.properties.map((p) => p.id);
        const propertyList = this.getByPropertyList(propertyIds);

        const allProperties = new PropertyBuilder<ExtraPropertyKeys, ScoreWithStatistics>({
            listOfProperties: propertyList,
            propertiesScore: propertyScores ?? {},
            postElementAndCampaign: this.post,
            precision: this.precision,
        }).build();

        return allProperties;
    };

    public getByHeadings = () => {
        const scores = this._scoresWithStats;

        const allHeadingsValues = ScoreDetailBuilder.drilldownDimension(scores?.dimensions, "headings");

        let totalFromHeadings: ScoreValue = null;
        const headingScores = this.ssq.headings.map((heading) => {
            const headingScore = allHeadingsValues?.[heading.id];
            totalFromHeadings = ScoreService.addScore(totalFromHeadings, headingScore?.score.value ?? null);
            return {
                id: heading.id,
                title: heading.title,
                score: ScoreService.normalizeScore(headingScore?.score, this.precision),
            };
        });

        const isMonolithic = this.ssq.monolithic;
        const totalScore = isMonolithic ? scores?.score : totalFromHeadings;

        return {
            total: ScoreService.normalizeScore(totalScore, this.precision),
            headings: headingScores,
        };
    };

    public getByHeadingId = (options: { headingId: number }) => {
        const { headingId } = options;
        const headingData = this._getByHeadingId(headingId);
        if (headingData === null) return null;

        const { heading, headingScore } = headingData;

        const subheadingDimensions = ScoreDetailBuilder.drilldownDimension(headingScore.dimensions, "subheadings");
        const subheadings = heading.children.map((subheading) => {
            const subheadingScore = subheadingDimensions?.[subheading.id];

            return {
                id: subheading.id,
                title: subheading.title,
                score: ScoreService.normalizeScore(subheadingScore?.score, this.precision),
            };
        });

        const properties = this.handlePropertyDimension(headingData);

        const totalScore = ScoreService.normalizeScore(headingScore.score, this.precision);
        return {
            total: totalScore,
            subheadings: subheadings,
            properties: properties,
        };
    };

    public getBySubheadingId = (options: { headingId: number; subheadingId: number }) => {
        const { headingId, subheadingId } = options;
        const headingData = this._getByHeadingId(headingId);
        if (headingData === null) return null;

        const subheadingData = this._getBySubheadingId(subheadingId, headingData);
        if (subheadingData === null) return null;

        const properties = this.handlePropertyDimension(subheadingData);

        const total = ScoreService.normalizeScore(subheadingData.headingScore.score, this.precision);
        return {
            total: total,
            properties: properties,
        };
    };

    public getByPropertyId = (options: {
        headingId: number;
        subheadingId?: number | undefined;
        propertyId: number;
    }) => {
        const { headingId, subheadingId, propertyId } = options;

        const headingData = this._getByHeadingId(headingId);
        const headingForProperties =
            subheadingId === undefined ? headingData : this._getBySubheadingId(subheadingId, headingData);

        if (headingForProperties === null) return null;

        const properties = this.handlePropertyDimension(headingForProperties);
        const property = properties?.find((property) => property.property.id === propertyId);
        if (property === undefined) return null;

        return {
            property: property,
        };
    };

    /**
     * Internals below
     */

    private _getByHeadingId = (headingId: number): _InternalHeading | null => {
        const scores = this._scoresWithStats;

        const _headingDimension = ScoreDetailBuilder.drilldownDimension(scores?.dimensions, "headings");
        const headingScore = _headingDimension?.[headingId];
        const heading = this.ssq.headings.find((h) => h.id === headingId);

        if (headingScore === undefined) return null;
        if (heading === undefined) return null;

        return { heading, headingScore };
    };

    private _getBySubheadingId = (
        subheadingId: number,
        internalHeading: _InternalHeading | null,
    ): _InternalHeading | null => {
        if (internalHeading === null) return null;
        const { heading, headingScore } = internalHeading;

        const subheadingDimensions = ScoreDetailBuilder.drilldownDimension(headingScore.dimensions, "subheadings");
        const subheadingScore = subheadingDimensions?.[subheadingId];
        const subheading = heading?.children.find((subheading) => subheading.id === subheadingId);

        if (subheadingScore === undefined) return null;
        if (subheading === undefined) return null;

        return { heading: subheading, headingScore: subheadingScore };
    };

    private getByPropertyList = (ids: number[]): PropertyWithIndexes[] => {
        const acc: PropertyWithIndexes[] = [];
        const propertyList = ids.reduce((prevAcc, currentId) => {
            const existingProperty = this._allPropertyByIds?.[currentId];
            if (existingProperty === undefined) return prevAcc;
            const propertyWithIndexes: PropertyWithIndexes = {
                ...existingProperty,
                extraIndexes: {},
            };

            prevAcc.push(propertyWithIndexes);
            return prevAcc;
        }, acc);

        return propertyList;
    };

    private propertyListToMapping = (properties: SsqProperty[], existingAcc: PropertyMapping): PropertyMapping => {
        const _propertyAcc: PropertyMapping = existingAcc;
        const propertyAcc = properties.reduce((acc, property) => {
            acc["byProperty"][property.id] = property;
            return acc;
        }, _propertyAcc);
        return propertyAcc;
    };

    private preparePropertyIdMapping = (ssq: Ssq) => {
        const acc: PropertyMapping = { byHeadingId: {}, byProperty: {} };
        const allPropertyByIds = ssq.headings.reduce((accHeading, heading) => {
            const _accHeadingWithProperties = this.propertyListToMapping(heading.properties, accHeading);

            const _accHeadingWithSubheadings = heading.children.reduce((_accHeading2, subheading) => {
                const _accSubheadingWithProperties = this.propertyListToMapping(subheading.properties, accHeading);
                return _accSubheadingWithProperties;
            }, _accHeadingWithProperties);

            return _accHeadingWithSubheadings;
        }, acc);
        return allPropertyByIds;
    };
}
